import { Machine, assign } from "xstate";

import {
  IDEAL_X_ANGLE,
  IDEAL_Y_FRONT_ANGLE,
  IDEAL_Y_LEFT_ANGLE,
  IDEAL_Y_RIGHT_ANGLE,
} from "./MugShotConfig";

const allImagesCaptured = (context, event) => {
  const frontImageCaptured = context.frontImage !== undefined || event.currentImageName.toLowerCase() === "front";
  const leftImageCaptured = context.leftImage !== undefined || event.currentImageName.toLowerCase() === "left";
  const rightImageCaptured = context.rightImage !== undefined || event.currentImageName.toLowerCase() === "right";
  return frontImageCaptured && leftImageCaptured && rightImageCaptured;
};

 const assignCurrentImage = (currentImageName, currentImage, currentImageData) => {
  switch(currentImageName.toLowerCase()) {
    case "front":
      return {frontImage: currentImage, frontImageData: currentImageData};
    case "left":
      return {leftImage: currentImage, leftImageData: currentImageData};
    case "right":
      return {rightImage: currentImage, rightImageData: currentImageData};
    default:
      return {};
  }
 };

const imagingMachine = Machine(
  {
    initial: "positioning",
    context: {
      positioningOnly: false,
      waitUntilReady: false,
      angleX: IDEAL_X_ANGLE,
      message: null,
      sound: null,

      // Image information in data URI string format (e.g. data:image/png;base64,... )
      frontImage: undefined,
      leftImage: undefined,
      rightImage: undefined,

      // Complementary ImageData objects (https://developer.mozilla.org/en-US/docs/Web/API/ImageData/ImageData)
      frontImageData: undefined,
      leftImageData: undefined,
      rightImageData: undefined,

    },
    states: {
      initialising: {
        on: {
          INITIALISED: [
            { target: "waiting", cond: "waitUntilReady" },
            { target: "positioning" },
          ],
        },
      },
      waiting: {
        on: {
          READY: "positioning",
        },
      },
      positioning: {
        on: {
          POSITIONED: [
            {
              target: "complete",
              cond: "positioningOnly"
            },
            {
              target: "positioning",
              cond: (context, event) => !allImagesCaptured(context, event),
              actions: assign( (context, event) => assignCurrentImage(event.currentImageName, event.currentImage, event.currentImageData)),
            },
            {
              target: "complete",
              cond: (context, event) => allImagesCaptured(context, event),
              actions: assign( (context, event) => assignCurrentImage(event.currentImageName, event.currentImage, event.currentImageData)),
            },
          ],
        },
      },
      complete: {
        type: "final",
        data: (ctx) => {
          return {
            frontImage: ctx.frontImage,
            leftImage: ctx.leftImage,
            rightImage: ctx.rightImage,
            frontImageData: ctx.frontImageData,
            leftImageData: ctx.leftImageData,
            rightImageData: ctx.rightImageData,
          };
        },
      },
    },
  },
  {
    guards: {
      positioningOnly: (context, event) => context.positioningOnly,
      waitUntilReady: (context, event) => context.waitUntilReady,
    },
  }
);

export const scanningMachine = Machine({
  initial: "initialising",
  context: {
    frontAngle: IDEAL_Y_FRONT_ANGLE,
    leftAngle: IDEAL_Y_LEFT_ANGLE,
    rightAngle: IDEAL_Y_RIGHT_ANGLE,

    // Image information in data URI string format (e.g. data:image/png;base64,... )
    frontImage: undefined,
    leftImage: undefined,
    rightImage: undefined,

    // Complementary ImageData objects (https://developer.mozilla.org/en-US/docs/Web/API/ImageData/ImageData)
    frontImageData: undefined,
    leftImageData: undefined,
    rightImageData: undefined,

    errors: {
      mouthOpen: false,
      glassesOn: false,
      eyesClosed: false,
      timeout: false
    },

    scan_info: undefined,
  },
  states: {
    initialising: {
      on: {
        INITIALISED: "capturing",
      },
    },

    lostposition: {
      entry: assign({
        inPosition: false,
      }),
    },

    timeout: {},

    error: {},

    glasses: {
      on: {
        TIMEOUT: "timeout",
        NO_GLASSES: "capturing",
      },
    },

    capturing: {
      on: {
        NOTPOSITIONED: "lostposition",
        TIMEOUT: "timeout",
        GLASSES_FOUND: "glasses",
        RESET: "initialising",
      },
      invoke: {
        id: "capturingImagingMachine",
        src: imagingMachine,
        data: (ctx, e) => ({
          ...imagingMachine.context,
          yAngles: {
            front: ctx.frontAngle,
            left: ctx.leftAngle,
            right: ctx.rightAngle
          },
          last_blur_measure: {
            front: 0,
            left:  0,
            right: 0
          },
        }),
        onDone: {
          target: "analyseImagesForErrors",
          actions: assign((context, event) => {
            return {
              frontImage: event.data.frontImage,
              leftImage: event.data.leftImage,
              rightImage: event.data.rightImage,
              frontImageData: event.data.frontImageData,
              leftImageData: event.data.leftImageData,
              rightImageData: event.data.rightImageData,
            }
          }),
        },
      },
    },

    analyseImagesForErrors : {
      on: {
        NEXT: "completeAfterBuffer",
        ERROR: "error"
      },
    },

    // An intermediate stage that we can wait at until MINIMUM_SCAN_TIME_IN_MILLISECONDS has passed.
    // For more details see MugShotConfig.js -> MINIMUM_SCAN_TIME_IN_MILLISECONDS
    completeAfterBuffer: {
      on: {
        NEXT: "complete",
      },
    },

    complete: {
      type: "final",
      data: (ctx) => {
        return (({ frontImage, leftImage, rightImage, scan_info, frontImageData, leftImageData, rightImageData }) => ({
          frontImage,
          leftImage,
          rightImage,
          scan_info,
          frontImageData,
          leftImageData,
          rightImageData
        }))(ctx);
      },
    },
  },
});
